import clsx from 'clsx';
import { useRef, useMemo, Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconSearch, IconAdjustments } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import DateRangePicker from '@/components/dateRangePicker';
import EmptyState from '@/components/EmptyState';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import ListSkeleton from '@/components/SkeletonLoader/ListSkeleton';
import CustomTable from '@/components/Table';
import { LessonStageConfig } from '@/constants/constant';
import { EExportType, ELessonStageType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  exportParentAnalytics,
  getParentAnalyticsGames,
  getParentAnalyticsGamesDropdown,
} from '@/service/reportService';
import { useParentStore } from '@/store/useParentStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { formatQuery, downloadFile, formatLastActive } from '@/utils';

import FilterDrawer, {
  type IParentReportFilters,
} from './components/FilterDrawer';

interface IParentReportApiRecord {
  id: string;
  title_en: string;
  title_ar: string;
  stage: number;
  accuracy: number;
  quarters?: number | string;
  rounds?: number | string;
  last_updated: string;
}

interface IParentReportRecord {
  id: string;
  title_en: string;
  title_ar: string;
  stage: number;
  accuracy: number;
  quarters: number | string;
  rounds: number | string;
  last_updated: number;
  game_image?: string;
}

interface IParentReportsForm {
  search: string;
}

const STAGE_LABEL_KEY = 'parent-reports.table.stage';

const ParentReportsAnalytics = () => {
  const { formatText } = useFormatText();
  const setTitle = usePageTitle((state) => state.setTitle);
  const { settings } = useSettingsStore();
  const language = settings?.language;
  const { selectedChild } = useParentStore();
  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();

  const [analyticsData, setAnalyticsData] = useState<IParentReportRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'' | 'ASC' | 'DESC'>('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [filters, setFilters] = useState<IParentReportFilters>({
    game: [],
    stage: [],
  });
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [gameOptions, setGameOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const isInitialLoad = useRef(true);

  const { register, watch } = useForm<IParentReportsForm>({
    defaultValues: {
      search: '',
    },
  });

  const pageTitle = formatText('parent-reports.title');

  useEffect(() => {
    setTitle(pageTitle);
    return () => setTitle('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const fetchGameOptions = async () => {
    try {
      const response = await getParentAnalyticsGamesDropdown();
      if (response?.status) {
        const isArabic = language === 'ar';
        const options = (response?.data ?? []).map(
          (item: {
            id: string;
            title_en: string;
            title_ar: string;
            quarters?: number | string;
            rounds?: number | string;
          }) => {
            const baseLabel = isArabic ? item?.title_ar : item?.title_en;
            const hasQuarter =
              item?.quarters !== undefined &&
              item?.quarters !== null &&
              item?.quarters !== '';
            const hasRound =
              item?.rounds !== undefined &&
              item?.rounds !== null &&
              item?.rounds !== '';
            const tags = [
              hasQuarter ? `Q${item?.quarters}` : null,
              hasRound ? `R${item?.rounds}` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return {
              value: item?.id,
              label: tags ? `${baseLabel} · ${tags}` : baseLabel,
            };
          }
        );
        setGameOptions(options);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchGameOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const searchQuery = watch('search');
  const debouncedSearch = useDebounce(searchQuery, 500);

  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters, startDate, endDate]);

  const setLoadingState = (loading: boolean) => {
    if (isInitialLoad.current) setScreenLoading(loading);
    else setTableLoading(loading);
  };

  const fetchAnalytics = async () => {
    setLoadingState(true);
    try {
      const params: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...(selectedChild && { child_id: selectedChild.id }),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(sortField &&
          sortDirection && {
            [`order[${sortField}]`]: sortDirection,
          }),
        ...(filters.game.length > 0 && { game: filters.game }),
        ...(filters.stage.length > 0 && { stage: filters.stage }),
        ...(startDate && {
          start_date: Math.floor(new Date(startDate).getTime() / 1000),
        }),
        ...(endDate && {
          end_date: Math.floor(new Date(endDate).getTime() / 1000),
        }),
      };

      const query = formatQuery(params);
      const response = await getParentAnalyticsGames(query);
      if (response?.status && response?.data?.length > 0) {
        const records: IParentReportRecord[] = (response.data ?? []).map(
          (item: IParentReportApiRecord) => ({
            id: item.id,
            title_en: item?.title_en,
            title_ar: item?.title_ar,
            stage: item?.stage,
            accuracy: item?.accuracy ?? 0,
            quarters: item?.quarters ?? '-',
            rounds: item?.rounds ?? '-',
            last_updated: item?.last_updated,
          })
        );
        setAnalyticsData(records);
        setTotal(response.total ?? 0);
      } else {
        setAnalyticsData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error(error);
      setAnalyticsData([]);
      setTotal(0);
    } finally {
      setLoadingState(false);
      isInitialLoad.current = false;
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearch,
    sortField,
    sortDirection,
    filters,
    startDate,
    endDate,
    selectedChild,
    pageSize,
  ]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortField('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  const reportColumns = useMemo(
    () => [
      {
        key: 'game_name',
        title: 'parent-reports.table.game-name',
        width: '300px',
        sorter: true,
        handleSort: () => handleSort('game_name'),
        render: (record: IParentReportRecord) => {
          const gameName =
            language === 'ar' ? record?.title_ar : record?.title_en;
          return (
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-light-100">
                <UserAvatarList
                  user={gameName}
                  isSquare={true}
                  image={record.game_image}
                  className="h-10 w-10 text-neutral-primary"
                />
              </div>
              <span className="text-sm font-medium text-neutral-primary">
                {gameName}
              </span>
            </div>
          );
        },
      },
      {
        key: 'quarter',
        title: 'parent-reports.table.quarter',
        width: '120px',
        sorter: true,
        handleSort: () => handleSort('quarters'),
        render: (record: IParentReportRecord) => (
          <span className="text-sm text-neutral-secondary">
            {record.quarters ?? '-'}
          </span>
        ),
      },
      {
        key: 'round',
        title: 'parent-reports.table.round',
        width: '120px',
        sorter: true,
        handleSort: () => handleSort('rounds'),
        render: (record: IParentReportRecord) => (
          <span className="text-sm text-neutral-secondary">
            {record.rounds ?? '-'}
          </span>
        ),
      },
      {
        key: 'stage',
        title: STAGE_LABEL_KEY,
        width: '220px',
        sorter: true,
        handleSort: () => handleSort('stage'),
        render: (record: IParentReportRecord) => {
          const config = LessonStageConfig[record.stage as ELessonStageType];
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${config?.className ?? 'bg-neutral-light-50 text-neutral-secondary'}`}
            >
              {config
                ? formatText(config.label)
                : `${formatText(STAGE_LABEL_KEY)} ${record.stage}`}
            </span>
          );
        },
      },
      {
        key: 'accuracy',
        title: 'parent-reports.table.accuracy',
        width: '200px',
        sorter: true,
        // handleSort: () => handleSort('accuracy'),
        render: (record: IParentReportRecord) => (
          <span className="text-sm text-neutral-primary">
            {record?.accuracy || 0}%
          </span>
        ),
      },
      {
        key: 'last_updated',
        title: 'parent-reports.table.last-update',
        width: '180px',
        sorter: true,
        handleSort: () => handleSort('last_updated'),
        render: (record: IParentReportRecord) => (
          <span className="text-sm text-neutral-secondary whitespace-nowrap">
            {formatLastActive(record?.last_updated, formatText)}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortField, sortDirection, language]
  );

  const handleExport = async (type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {
        ...(selectedChild && { child_id: selectedChild.id }),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(filters.game.length > 0 && { game: filters.game }),
        ...(filters.stage.length > 0 && { stage: filters.stage }),
        ...(selectedRowKeys.length > 0 && { ids: selectedRowKeys }),
        ...(startDate && {
          start_date: Math.floor(new Date(startDate).getTime() / 1000),
        }),
        ...(endDate && {
          end_date: Math.floor(new Date(endDate).getTime() / 1000),
        }),
      };

      if (type === 'csv') {
        query.doc_type = EExportType.CSV;
      } else if (type === 'pdf') {
        query.doc_type = EExportType.PDF;
      }

      const response = await exportParentAnalytics(query);
      if (response) {
        const label = pageTitle?.replace(/\s+/g, '_')?.toLowerCase();
        downloadFile(response, `${label}_${new Date().getTime()}.${type}`);
      }
    } catch (error) {
      console.error('Error exporting content:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const isFilterApplied =
    filters?.game?.length > 0 ||
    filters?.stage?.length > 0 ||
    !!startDate ||
    !!endDate;

  const showListUI =
    isScreenLoading ||
    isTableLoading ||
    analyticsData?.length > 0 ||
    searchQuery?.length > 0 ||
    !!debouncedSearch ||
    isFilterApplied;

  const isEmptySearch =
    !isScreenLoading &&
    !isTableLoading &&
    analyticsData?.length === 0 &&
    (!!debouncedSearch || isFilterApplied);

  if (!showListUI) {
    return (
      <EmptyState
        title={formatText('parent-reports.empty-title')}
        description={formatText('parent-reports.empty-description')}
        imageSrc="/assets/svg/empty-report-analytics.svg"
        className="h-full!"
      />
    );
  }

  if (isScreenLoading) {
    return <ListSkeleton columns={reportColumns} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4 flex-wrap ps-1">
        <div className="flex flex-1 items-center gap-3 flex-wrap">
          <Input
            name="search"
            register={register}
            placeholder={formatText('parent-reports.search-placeholder')}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-70"
            inputClassName="py-[13px]"
          />

          <div className="relative">
            <Button
              variant="outline"
              label={formatText('parent-reports.filters.button')}
              icon={
                <IconAdjustments
                  size={16}
                  className={
                    isFilterApplied ? 'text-brand-default' : 'text-neutral-500'
                  }
                />
              }
              className={clsx(
                'h-12 px-4 min-w-23.25 text-sm font-semibold',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <DateRangePicker
            setFirstDate={setStartDate}
            setSecondDate={setEndDate}
            popupAlign="right"
          />
          <ExportDropdown
            onCsvExport={() => handleExport('csv')}
            onPdfExport={() => handleExport('pdf')}
            disabled={analyticsData?.length === 0 || isExportLoading}
          />
        </div>
      </div>

      <CustomTable
        dataSource={analyticsData}
        columns={reportColumns}
        isEmptySearch={isEmptySearch}
        rowKey="id"
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        total={total}
        pageSize={pageSize}
        fieldName={sortField}
        sortDirection={sortDirection}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        isLoading={isTableLoading}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      <Activity mode={isFilterOpen ? 'visible' : 'hidden'}>
        <FilterDrawer
          isOpen={isFilterOpen}
          setIsOpen={setIsFilterOpen}
          initialFilters={filters}
          gameOptions={gameOptions}
          onApplyFilters={setFilters}
        />
      </Activity>
    </div>
  );
};

export default ParentReportsAnalytics;
